import { FormHelperText, styled, Typography } from '@ori-ui/mui';
import {
  useApiClient,
  createCustomerRegistration,
  HttpStatusCodes,
  useGetApplicationsSimplifiedRegistrationRequestValidation,
} from '@ori/global-api-client-react';
import type { AxiosError, GlobalCustomerRegistrationsRegistrationDto } from '@ori/global-api-client-react';
import type { AxiosRequestConfig } from 'axios';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import type { SubmitHandler } from 'react-hook-form';

import { Localization } from '../../Localization';
import { defaultTranslations } from '../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { useConfiguration, useFormError, useNotificationData } from '../../contexts';
import type { IErrors } from '../../interfaces';
import { MessageType } from '../../interfaces';
import { FetchResult, TranslationKey } from '../../models';
import { useRegistrationsMetadata } from '../../models/api';
import { getErrorLocalizationByCode } from '../../utils';
import { PreStepForm } from '../Forms';

const RootContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  paddingTop: theme.spacing(10),
}));

const WrapperCaptcha = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

export const PreStep: FC = () => {
  const { data: registrationConfig } = useRegistrationsMetadata();
  const { data: captcha, isValidating } = useGetApplicationsSimplifiedRegistrationRequestValidation();

  const [submitSuccess, setSubmitSuccess] = useState(false);

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const {
    context: { notificationData },
  } = useNotificationData();
  const { setNotificationData } = notificationData;

  const {
    context: { errorData },
  } = useFormError();
  const { setErrorData, customErrors } = errorData;

  const submitErrorLoginType = customErrors?.find((er) => er.fieldName === 'LoginType');

  const errorMessage = getErrorLocalizationByCode(submitErrorLoginType?.errorCode);

  const { registrationType, loginRedirectUrl, referral } = useConfiguration();

  const api = useApiClient();

  const showCaptcha = !isValidating && captcha?.siteKey && captcha.isMandatory;

  const onSubmit: SubmitHandler<GlobalCustomerRegistrationsRegistrationDto> = async (data) => {
    const captchaToken = await recaptchaRef.current?.executeAsync();

    const headers: AxiosRequestConfig['headers'] = {
      'x-response-key': captchaToken ?? '',
    };

    const isNotSocialNetwork = data.loginType === 'Email' || data.loginType === 'Telephone';

    try {
      const currentData: GlobalCustomerRegistrationsRegistrationDto = {
        ...data,
        contactEmail: isNotSocialNetwork ? data.contactEmail : undefined,
        contactTelephone: isNotSocialNetwork ? data.contactTelephone : undefined,
        password: isNotSocialNetwork ? data.password : undefined,
        customerId: referral?.CustomerId,
        currentStep: 'Prestep',
        startDate: new Date(),
        sponsorOrigin: referral?.ReferralType,
        sponsorNumber: referral?.ConsultantNumber,
        registrationType,
      };
      const createResponse = await createCustomerRegistration(api, currentData, headers);

      const registrationId = createResponse.data;
      const result = new FetchResult<unknown>(createResponse.status);

      if (result.isSuccess()) {
        setSubmitSuccess(true);
        if (customErrors && customErrors.length > 0) {
          setErrorData([]);
        }
        const redirectUrl = `${loginRedirectUrl}?registrationId=${registrationId}`;

        const form = document.createElement('form');
        const password = document.createElement('input');
        form.method = 'POST';
        form.action = redirectUrl;
        form.style.display = 'none';
        password.name = 'Password';
        password.value = data.password ?? '';
        form.append(password);
        document.body.append(form);
        form.submit();
      }
    } catch (error: unknown) {
      const errorResponse = error as AxiosError;
      if (errorResponse.response?.status === HttpStatusCodes.status400BadRequest) {
        const { Errors } = errorResponse.response.data as IErrors;
        const submitErrors = Errors?.map((er) => ({
          fieldName: er.PropertyName,
          errorCode: er.Code,
        }));
        if (submitErrors?.length && submitErrors.length > 0) {
          setErrorData(submitErrors);
        } else {
          setErrorData([]);
        }
      } else {
        setNotificationData({
          isOpen: true,
          message: (
            <Localization
              id={TranslationKey.cannotUpdate}
              defaultMessage={defaultTranslations.cannotUpdate}
            />
          ),
          type: MessageType.error,
        });
      }
    }
  };

  return (
    <RootContainer>
      <Typography
        variant="h4"
        sx={{ fontWeight: 'bold' }}
        color="textPrimary"
      >
        <Localization
          id={TranslationKey.Prestep_Header}
          defaultMessage={defaultTranslations.Prestep_Header}
        />
      </Typography>
      {submitErrorLoginType ? (
        <FormHelperText error>
          <Localization {...errorMessage} />
        </FormHelperText>
      ) : null}
      <PreStepForm
        config={registrationConfig}
        isSuccessSubmit={submitSuccess}
        onSubmitForm={onSubmit}
      />
      {showCaptcha ? (
        <WrapperCaptcha>
          <ReCAPTCHA
            ref={recaptchaRef}
            size="invisible"
            badge="inline"
            sitekey={captcha.siteKey ?? ''}
          />
        </WrapperCaptcha>
      ) : null}
    </RootContainer>
  );
};
